import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  CircularProgress,
  Button,
  Grid,
  Card,
  CardContent,
  Alert
} from '@mui/material';
import { Home, Refresh } from '@mui/icons-material';
import { fetchSessionResults, SessionResult } from '../services/testSessionService';
import Header from '../components/Header';
import SessionReviewPanel from '../components/SessionReviewPanel';
import DiagnosticTeaser from '../components/DiagnosticTeaser';
import RankPanel from '../components/RankPanel';
import CertificatePanel from '../components/CertificatePanel';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { selectCurrentSession } from '../store/slices/sessionSlice';

// Extended interface for display purposes
interface DisplaySessionResult extends SessionResult {
  testTitle: string;
  correctAnswers: number;
  percentage: number;
  timeSpent: number;
  submittedAt: string;
}

const ResultsPage: React.FC = () => {
  const navigate = useNavigate();
  const [results, setResults] = useState<DisplaySessionResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const selectedStudent = useSelector((state: RootState) => state.students.selectedStudent);
  const currentSession = useSelector(selectCurrentSession);

  // Add ref to track if API call is in progress
  const isFetchingResultsRef = useRef(false);

  useEffect(() => {
    const loadResults = async () => {
      if (!currentSession?.id) {
        // No current session - redirect to dashboard
        setError('No active session found. Please start a test first.');
        setLoading(false);
        return;
      }

      if (isFetchingResultsRef.current) {
        return;
      }

      try {
        isFetchingResultsRef.current = true;
        const sessionResults = await fetchSessionResults(currentSession.id);
        
        if (sessionResults) {
          // Transform the service result to match our display needs
          const transformedResult: DisplaySessionResult = {
            ...sessionResults,
            // Use series name if available, otherwise default to subject or generic title
            testTitle: currentSession.series || currentSession.subject || 'Test Results',
            correctAnswers: sessionResults.questionResults.filter(q => q.isCorrect).length,
            // Guard against a package with totalScore 0 — the divide would render
            // "NaN%" and flag the card red. Treat "not scored" as 0%.
            percentage: sessionResults.totalScore > 0
              ? (sessionResults.score / sessionResults.totalScore) * 100
              : 0,
            timeSpent: sessionResults.endTime && sessionResults.startTime 
              ? Math.floor((new Date(sessionResults.endTime).getTime() - new Date(sessionResults.startTime).getTime()) / 1000)
              : 0,
            submittedAt: sessionResults.endTime || sessionResults.startTime
          };
          setResults(transformedResult);
        } else {
          setError('No results found for this session');
        }
      } catch (err) {
        setError('Failed to load test results');
      } finally {
        setLoading(false);
        isFetchingResultsRef.current = false;
      }
    };

    loadResults();
  }, [currentSession?.id, currentSession?.series, currentSession?.subject]);

  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${remainingSeconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    } else {
      return `${remainingSeconds}s`;
    }
  };

  const getScoreColor = (percentage: number): string => {
    if (percentage >= 80) return '#4caf50'; // Green
    if (percentage >= 60) return '#ff9800'; // Orange
    return '#f44336'; // Red
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
        {selectedStudent && <Header selectedStudent={selectedStudent} />}
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress size={60} />
          </Box>
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
        {selectedStudent && <Header selectedStudent={selectedStudent} />}
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="contained"
              startIcon={<Home />}
              onClick={() => navigate('/dashboard')}
            >
              Back to Dashboard
            </Button>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => window.location.reload()}
            >
              Retry
            </Button>
          </Box>
        </Container>
      </Box>
    );
  }

  if (!results) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
        {selectedStudent && <Header selectedStudent={selectedStudent} />}
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h4" gutterBottom color="primary">
              🎉 Test Completed Successfully!
            </Typography>
            <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
              Your test has been submitted and is being processed.
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
              Results will be available shortly. You can check back later or return to the dashboard.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<Home />}
                onClick={() => navigate('/dashboard')}
              >
                Back to Dashboard
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {selectedStudent && <Header selectedStudent={selectedStudent} />}
      
      <Container maxWidth="md" sx={{ py: 4 }}>
        {/* Header */}
        <Paper sx={{ p: 3, mb: 3, textAlign: 'center' }}>
          <Typography variant="h4" gutterBottom>
            {currentSession?.grade && currentSession?.subject 
              ? `${currentSession.grade} - ${currentSession.subject}` 
              : 'Grade and Subject'}
          </Typography>
          <Typography variant="h6" color="text.secondary">
            {results.testTitle !== 'Test Results' ? results.testTitle : 'Series Name'}
          </Typography>
        </Paper>

        {/* Score Overview */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" gutterBottom>
            Score Overview
          </Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card sx={{ textAlign: 'center', bgcolor: getScoreColor(results.percentage), color: 'white' }}>
                <CardContent>
                  <Typography variant="h4">
                    {results.percentage.toFixed(1)}%
                  </Typography>
                  <Typography variant="body2">
                    Overall Score
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card sx={{ textAlign: 'center' }}>
                <CardContent>
                  <Typography variant="h4" color="primary">
                    {results.correctAnswers}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Correct Answers
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card sx={{ textAlign: 'center' }}>
                <CardContent>
                  <Typography variant="h4">
                    {results.answeredQuestions}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Questions Attempted
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card sx={{ textAlign: 'center' }}>
                <CardContent>
                  <Typography variant="h4">
                    {formatTime(results.timeSpent)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Time Spent
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>

        {/* Detailed Stats */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" gutterBottom>
            Detailed Statistics
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 6 }}>
              <Typography variant="body1">
                <strong>Total Questions:</strong> {results.totalQuestions}
              </Typography>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Typography variant="body1">
                <strong>Questions Answered:</strong> {results.answeredQuestions}
              </Typography>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Typography variant="body1">
                <strong>Correct Answers:</strong> {results.correctAnswers}
              </Typography>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Typography variant="body1">
                <strong>Incorrect Answers:</strong> {results.answeredQuestions - results.correctAnswers}
              </Typography>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Typography variant="body1">
                <strong>Unanswered:</strong> {results.totalQuestions - results.answeredQuestions}
              </Typography>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Typography variant="body1">
                <strong>Submitted At:</strong> {new Date(results.submittedAt).toLocaleString()}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* The diagnostic hook (feature #08). Mounted first because it is the reason a
            student reads any of the panels below: it names the weak skill and gives
            each locked panel its own CTA carrying that panel's SKU to checkout. */}
        {currentSession?.id && <DiagnosticTeaser sessionId={currentSession.id} />}

        {/* Where this score placed (feature #02). Renders its own "too few attempts"
            state, so it is safe to mount before a cohort exists. */}
        {currentSession?.packageId && (
          <RankPanel packageId={currentSession.packageId} score={results.score} />
        )}

        {/* Paid review (feature #01). Renders its own locked/upsell state on a 402,
            so it is safe to mount unconditionally for every completed attempt. */}
        {currentSession?.id && (
          <SessionReviewPanel
            sessionId={currentSession.id}
            onUpgradeClick={() => navigate('/checkout?sku=solutions')}
          />
        )}

        {/* Certificate (feature #04). Issues on demand — see CertificatePanel for why
            this is not automatic on submission. */}
        {currentSession?.id && (
          <CertificatePanel
            sessionId={currentSession.id}
            studentName={selectedStudent?.name}
          />
        )}

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3 }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<Home />}
            onClick={() => navigate('/dashboard')}
          >
            Back to Dashboard
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default ResultsPage;